(()=>{
  // ---------- Utils ----------
  const $  = (sel, root=document) => root.querySelector(sel);
  const $$ = (sel, root=document) => Array.from(root.querySelectorAll(sel));
  // ----- Références "NNN.NNN" : matching avec OU sans le point -----
  const refKey    = (s) => (s || "").toString().replace(/\D/g, "");       // chiffres seuls : "110.193" et "110193" -> "110193"
  const isRefLike = (s) => /^\s*\d{3}\.?\d{3}\s*$/.test(s || "");         // saisie ressemblant à une référence
  const refQuery  = (s) => {                                             // forme canonique "NNN.NNN" pour interroger Shopify
    const d = refKey(s);
    return d.length === 6 ? `${d.slice(0,3)}.${d.slice(3)}` : (s || "").trim();
  };

  // Signal combiné : s'annule si le signal externe s'annule OU au bout de `ms` (timeout)
  const withTimeout = (extSignal, ms = 12000) => {
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), ms);
    const onExt = () => ctrl.abort();
    if (extSignal) {
      if (extSignal.aborted) ctrl.abort();
      else extSignal.addEventListener("abort", onExt, { once: true });
    }
    return { signal: ctrl.signal, done: () => { clearTimeout(to); if (extSignal) extSignal.removeEventListener("abort", onExt); } };
  };

  // Exécute fn sur chaque item, au plus `limit` en parallèle, en préservant l'ordre.
  const mapLimit = async (items, limit, fn, signal) => {
    const out = new Array(items.length);
    let i = 0;
    const worker = async () => {
      while (i < items.length) {
        if (signal?.aborted) return;
        const idx = i++;
        out[idx] = await fn(items[idx], idx);
      }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return out;
  };

  const fetchJSON = async (url, extra={}) => {
    const t = withTimeout(extra.signal, extra.timeout);
    try {
      const res = await fetch(url.toString(), {
        headers: { "Accept": "application/json", ...extra.headers },
        cache: extra.cache || "no-store",
        signal: t.signal
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    } finally {
      t.done();
    }
  };

  // /products/{handle}[?...] -> "handle"
  const extractHandle = (p) => {
    const raw = p?.url || (p?.handle ? `/products/${p.handle}` : "");
    try {
      const u = new URL(raw, location.origin);
      return u.pathname.replace(/^\/products\//, "").replace(/\/$/, "");
    } catch {
      return p?.handle || "";
    }
  };

  // ---------- Endpoints ----------
  const predictiveUrl = (q, limit) => {
    const base = (window.Shopify?.routes?.predictive_search_url) || "/search/suggest.json";
    const url = new URL(base, location.origin);
    url.searchParams.set("q", q);
    url.searchParams.set("resources[type]", "product");
    // Large pour paginer ensuite côté client
    url.searchParams.set("resources[limit]", String(Math.max(limit * 4, 40)));
    url.searchParams.set("resources[options][fields]", "title,tag,variants.sku");
    return url;
  };

  // Variantes + images via /products/{handle}.js
  const fetchProductJS = async (handle, signal) => {
    if (!handle) return null;
    const url = new URL(`/products/${handle}.js`, location.origin);
    return fetchJSON(url, { signal });
  };

  // PDFs en BATCH via /search?view=docs-bulk&q=... (rend une map {handle: {manual_pdf, datasheet_pdf, url}})
  const fetchDocsBulk = async (q, sectionRoot, signal) => {
    if (!q || q.trim().length < 2) return {};
    const url = new URL("/search", location.origin);
    url.searchParams.set("view", "docs-bulk");
    url.searchParams.set("q", q);
    url.searchParams.set("type", "product");
    url.searchParams.set("_", String(Date.now())); // anti-cache

    // preview_theme_id depuis la section ou l'URL
    const fromData = sectionRoot?.getAttribute("data-preview-theme-id") || "";
    const m = /[?&]preview_theme_id=(\d+)/.exec(location.search);
    const fromUrl = m ? m[1] : "";
    const previewId = fromData || fromUrl;
    if (previewId) url.searchParams.set("preview_theme_id", previewId);

    const data = await fetchJSON(url, { signal, headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" }});
    const map = {};
    (data?.products || []).forEach(p => {
      map[p.handle] = {
        manual_pdf: p.manual_pdf || null,
        datasheet_pdf: p.datasheet_pdf || null,
        url: p.url || (`/products/${p.handle}`)
      };
    });
    return map;
  };

  // PDFs par produit via /products/{handle}?view=docs-json (preview puis fallback live)
  const fetchProductDocsMeta = async (handle, sectionRoot, signal) => {
    if (!handle) return { title: "", url: `/products/${handle}`, variants: [], _dbg: {} };

    const buildUrl = (h, withPreview) => {
      const u = new URL(`/products/${h}`, location.origin);
      u.searchParams.set("view", "docs-json");
      u.searchParams.set("_", String(Date.now())); // anti-cache
      if (withPreview) {
        const fromData = sectionRoot?.getAttribute("data-preview-theme-id") || "";
        const m = /[?&]preview_theme_id=(\d+)/.exec(location.search);
        const fromUrl = m ? m[1] : "";
        const previewId = fromData || fromUrl;
        if (previewId) u.searchParams.set("preview_theme_id", previewId);
      }
      return u;
    };

    const fetchOnce = async (url) => {
      const t = withTimeout(signal);
      let text;
      try {
        const r = await fetch(url.toString(), {
          // PAS d'Accept: application/json -> sinon Shopify renvoie l'API produit
          // {"product":{...}} et IGNORE ?view=docs-json. On force le rendu du template.
          headers: { "Accept": "text/html", "Cache-Control": "no-cache", "Pragma": "no-cache" },
          cache: "no-store",
          signal: t.signal
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        text = await r.text();
      } finally {
        t.done();
      }
      let data = null;
      try { data = JSON.parse(text); } catch(e) {
        console.warn("[docs-hub] JSON parse fail for", url.toString(), text);
        throw e;
      }
      return {
        title: data?.title || "",
        url: data?.url || url.pathname.replace(/\/$/, ""),
        image: data?.image || null,
        variants: Array.isArray(data?.variants) ? data.variants : [],
        _dbg: { url: url.toString() }
      };
    };

    try {
      // 1) avec preview
      let meta = await fetchOnce(buildUrl(handle, true));
      const hasAnyPdf = (meta.variants || []).some(v => v.manual_pdf || v.datasheet_pdf);
      if (hasAnyPdf) return meta;
      // 2) fallback live
      meta = await fetchOnce(buildUrl(handle, false));
      return meta;
    } catch (e) {
      console.warn("[docs-hub] docs-json fetch failed", handle, e);
      return { title: "", url: `/products/${handle}`, variants: [], _dbg: { error: String(e) } };
    }
  };

  // ---------- Rendu d'une variante ----------
    const renderVariant = (tpl, product, docsMeta, variant) => {
    const node = tpl.content.firstElementChild.cloneNode(true);

    const vTitle = (variant?.title && variant.title !== "Default Title") ? ` - ${variant.title}` : "";
    const fullTitle = `${product?.title || ""}${vTitle}`.trim();

    // image
    const img = node.querySelector(".docs-result__media img");
    if (img) {
      const vImg = variant?.featured_image?.src;
      const pImg = Array.isArray(product?.images) && product.images.length ? product.images[0] : "";
      img.src = vImg || pImg || "";
      img.alt = fullTitle;
      img.loading = "lazy";
      img.decoding = "async";
    }

    // textes
    const refEl   = node.querySelector(".docs-result__ref");
    const titleEl = node.querySelector(".docs-result__title");
    if (refEl)   refEl.textContent   = variant?.sku || "";
    if (titleEl) titleEl.textContent = fullTitle;

    const actions = node.querySelector(".docs-result__actions");

    // lien produit — même onglet et placé EN DERNIER
    const link = node.querySelector(".docs-btn--link");
    if (link) {
      const baseUrl = docsMeta?.url || `/products/${product?.handle || ""}`;
      link.href = `${baseUrl}?variant=${variant?.id}`;
      link.removeAttribute("target");
      link.removeAttribute("rel");
    }

    // boutons + notes
    const aManual    = node.querySelector(".docs-btn--manual");
    const aDatasheet = node.querySelector(".docs-btn--datasheet");
    const nofile     = node.querySelector(".docs-result__nofile"); 

    // Renommer les boutons
    if (aManual)    aManual.textContent    = "Télécharger la Notice";
    if (aDatasheet) aDatasheet.textContent = "Télécharger la Fiche Technique";

    const hasManual    = !!(docsMeta && docsMeta.manual_pdf);
    const hasDatasheet = !!(docsMeta && docsMeta.datasheet_pdf);

    const showEl = (el, show) => {
      if (!el) return;
      if (show) el.classList.remove("is-hidden");
      else el.classList.add("is-hidden");
    };

    // Crée/retourne une note <em> accolée au bouton correspondant
    const ensureSiblingNote = (btnEl, className, text) => {
      if (!actions || !btnEl) return null;
      let note = node.querySelector(`.${className}`);
      if (!note) {
        note = document.createElement("em");
        note.className = `docs-note ${className}`;
        note.textContent = text;
        // place la note juste APRÈS le bouton (même si le bouton est masqué)
        btnEl.after(note);
      }
      return note;
    };
    const toggleNote = (noteEl, visible) => {
      if (!noteEl) return;
      noteEl.style.display = visible ? "inline-block" : "none";
    };

    // NOTICE
    const noteManual = ensureSiblingNote(aManual, "docs-note--manual", "Notice Indisponible");
    if (aManual) {
      if (hasManual) aManual.href = String(docsMeta.manual_pdf);
      showEl(aManual, hasManual);
    }
    toggleNote(noteManual, !hasManual);

    // FICHE TECHNIQUE
    const noteDatasheet = ensureSiblingNote(aDatasheet, "docs-note--datasheet", "Fiche Technique Indisponible");
    if (aDatasheet) {
      if (hasDatasheet) aDatasheet.href = String(docsMeta.datasheet_pdf);
      showEl(aDatasheet, hasDatasheet);
    }
    toggleNote(noteDatasheet, !hasDatasheet);

    // Lien produit en DERNIER (après boutons/notes)
    if (actions && link) actions.appendChild(link);

    // on n'affiche plus le message global
    if (nofile) nofile.style.display = "none";

    // debug
    node.dataset.hasManual = String(hasManual);
    node.dataset.hasDatasheet = String(hasDatasheet);

    return node;
  };

  // ---------- Init par section ----------
  $$(".documents-hub").forEach(section => {
    // Sécurité : si la classe documents-hub est dupliquée sur un wrapper parent,
    // on n'initialise QUE le composant interne (celui qui ne contient pas d'autre .documents-hub)
    // -> évite une double init (deux écouteurs submit, deux recherches concurrentes).
    if (section.querySelector(".documents-hub")) return;

    // Tabs robustes (délégation + état initial)
    try {
      const panels = $$(".docs-panel", section);

      const activateTab = (tab) => {
        const targetSel = tab?.getAttribute("data-target");
        if (!targetSel) return;
        const target = section.querySelector(targetSel);
        if (!target) return;
        $$(".docs-tab", section).forEach(t => t.classList.toggle("is-active", t === tab));
        panels.forEach(p => p.classList.toggle("is-active", p === target));
      };

      const currentActive = $(".docs-tab.is-active", section) || $(".docs-tab", section);
      if (currentActive) activateTab(currentActive);

      section.addEventListener("click", (ev) => {
        const btn = ev.target.closest(".docs-tab");
        if (!btn || !section.contains(btn)) return;
        ev.preventDefault();
        ev.stopPropagation();
        if (!btn.hasAttribute("type")) btn.setAttribute("type","button");
        activateTab(btn);
      }, { passive: false });
    } catch (e) {
      console.error("[docs-hub] tabs init error", e);
    }

    // Accordéons (+/-)
    try {
      $$(".docs-accordion summary", section).forEach(summary => {
        summary.addEventListener("click", () => {
          const details = summary.parentElement;
          const icon = summary.querySelector(".docs-accordion__icon");
          requestAnimationFrame(() => { if (icon) icon.textContent = details.open ? "-" : "+"; });
        }, { passive: true });
      });
    } catch (e) {
      console.error("[docs-hub] accordion init error", e);
    }

    // Recherche + pagination
    const form    = $(".docs-search", section);
    const input   = form ? $(".docs-search__input", form) : null;
    const results = form ? $(".docs-results", section) : null;
    const tpl     = $("template#docs-result-item", section);

    const pager   = $(".docs-pagination", section);
    const btnPrev = pager ? $(".docs-page-prev", pager) : null;
    const btnNext = pager ? $(".docs-page-next", pager) : null;
    const info    = pager ? $(".docs-page-info", pager) : null;

    const limit   = results ? parseInt(results.dataset.limit || "10", 10) : 10;

    const state = {
      items: [],      // { product, docsMeta, variant }[]
      page: 1,
      pageSize: limit,
      exactSkuMode: false,
      truncated: false
    };

    const updatePager = () => {
      if (!pager) return;
      if (state.exactSkuMode) { pager.hidden = true; return; }
      const total = state.items.length;
      const pages = Math.max(1, Math.ceil(total / state.pageSize));
      const page  = Math.min(Math.max(1, state.page), pages);
      state.page  = page;
      if (total <= state.pageSize) { pager.hidden = true; return; }
      pager.hidden = false;
      if (info) info.textContent = `Page ${page} / ${pages}`;
      if (btnPrev) btnPrev.disabled = (page <= 1);
      if (btnNext) btnNext.disabled = (page >= pages);
    };

    const renderPage = () => {
      if (!results || !tpl) return;
      results.innerHTML = "";
      if (!state.items.length) {
        results.innerHTML = "<p>Aucun résultat.</p>";
        updatePager();
        return;
      }

      if (state.exactSkuMode) {
        const slice = state.items.slice(0, state.pageSize);
        for (const {product, docsMeta, variant} of slice) {
          results.appendChild(renderVariant(tpl, product, docsMeta, variant));
        }
        updatePager();
        return;
      }

      const start = (state.page - 1) * state.pageSize;
      const end   = start + state.pageSize;
      const slice = state.items.slice(start, end);
      for (const {product, docsMeta, variant} of slice) {
        results.appendChild(renderVariant(tpl, product, docsMeta, variant));
      }
      if (state.truncated) {
        const note = document.createElement("p");
        note.className = "docs-results__note";
        note.textContent = "Beaucoup de résultats : affinez votre recherche (référence ou mot plus précis).";
        results.appendChild(note);
      }
      updatePager();
    };

    if (btnPrev) btnPrev.addEventListener("click", () => { state.page = Math.max(1, state.page - 1); renderPage(); });
    if (btnNext) btnNext.addEventListener("click", () => {
      const pages = Math.max(1, Math.ceil(state.items.length / state.pageSize));
      state.page = Math.min(pages, state.page + 1);
      renderPage();
    });

    const MAX_PRODUITS = 24;   // borne le nombre de produits traités (anti-cascade)
    const CONCURRENCE  = 5;    // requêtes simultanées max (anti rate-limit)
    let searchCtrl = null;     // permet d'annuler la recherche précédente

    const runSearch = async (rawQ) => {
      if (!form || !input || !results || !tpl) return;
      const q = (rawQ || "").trim();
      if (q.length < 2) { results.innerHTML = ""; if (pager) pager.hidden = true; return; }

      // Annule la recherche précédente encore en vol (évite les résultats incohérents)
      if (searchCtrl) searchCtrl.abort();
      searchCtrl = new AbortController();
      const signal = searchCtrl.signal;

      // Référence "NNN.NNN" : on interroge Shopify AVEC le point, on compare SANS le point
      const shopQ = isRefLike(q) ? refQuery(q) : q;
      const qKey  = refKey(q);

      results.innerHTML = "";

      try {
        // 1) prédictive → produits
        const data  = await fetchJSON(predictiveUrl(shopQ, state.pageSize), { signal });
        if (signal.aborted) return;
        const prods = data?.resources?.results?.products || [];
        if (!prods.length) {
          state.items = []; state.exactSkuMode = false; state.truncated = false;
          renderPage();
          return;
        }

        // 2) dédoublage par handle + borne
        const seenHandles = new Set();
        let handles = [];
        for (const p of prods) {
          const h = extractHandle(p);
          if (h && !seenHandles.has(h)) { seenHandles.add(h); handles.push(h); }
        }
        const truncated = handles.length > MAX_PRODUITS;
        if (truncated) handles = handles.slice(0, MAX_PRODUITS);

        // 3) Détails + PDFs PAR VARIANTE via docs-json (source unique), EN PARALLÈLE (concurrence limitée)
        const fetched = await mapLimit(handles, CONCURRENCE, async (handle) => {
          try {
            const meta = await fetchProductDocsMeta(handle, section, signal);
            if (!meta || !meta.variants || !meta.variants.length) return null;
            return { handle, meta };
          } catch (e) {
            if (signal.aborted) throw e;   // annulation : on stoppe
            return null;                    // un produit en échec n'arrête pas toute la recherche
          }
        }, signal);
        if (signal.aborted) return;

        // 4) un item PAR VARIANTE : docsMeta + image propres à la variante (lien produit gère ?variant)
        const exact = [];
        const all   = [];
        for (const r of fetched) {
          if (!r) continue;
          const meta = r.meta;
          const product = { title: meta.title, handle: r.handle, url: meta.url, images: meta.image ? [meta.image] : [] };
          for (const v of meta.variants) {
            const variant  = { id: v.id, title: v.title, sku: v.sku, featured_image: v.image ? { src: v.image } : null };
            const docsMeta = { manual_pdf: v.manual_pdf || null, datasheet_pdf: v.datasheet_pdf || null, url: meta.url };
            const item = { product, docsMeta, variant };
            all.push(item);
            if (qKey && refKey(v.sku) === qKey) exact.push(item);
          }
        }

        // 5) référence exacte → produit unique ; sinon liste paginée
        if (exact.length) {
          state.items = exact; state.exactSkuMode = true; state.truncated = false; state.page = 1;
          renderPage();
          return;
        }
        state.items = all; state.exactSkuMode = false; state.truncated = truncated; state.page = 1;
        renderPage();
      } catch (e) {
        if (signal.aborted || e?.name === "AbortError") return;  // annulation volontaire : silencieux
        console.error("[docs-hub] Search error:", e);
        results.innerHTML = "<p>Erreur de recherche. Réessayez.</p>";
        if (pager) pager.hidden = true;
      }
    };

    if (form && input) {
      form.addEventListener("submit", (ev) => { ev.preventDefault(); runSearch(input.value).catch(() => {}); });
      let debounce;
      input.addEventListener("input", () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => runSearch(input.value).catch(() => {}), 350);
      });
    }
  });
})();